import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Select, Skeleton, Statistic, Table } from 'antd';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LineChartOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { getLast30DaysRange } from '../../utils/dateUtils';
import {
  extractPagination,
  extractRows,
  getDateRangePickerValue,
  parseDateRangeChange,
  toNumber,
} from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import ChartEmptyState from './ChartEmptyState';
import { formatReportMoneyFa, formatReportTableAmount, REPORT_CHART_COLORS } from './chartFormatters';
import { FinanceLineTooltip } from './ReportChartTooltips';
import ClientSelect from '../../components/common/ClientSelect';

const FinanceReport = () => {
  const [filters, setFilters] = useState(() => {
    const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
    return { ...range, project_id: 'all', client_id: 'all', page: 1, per_page: 15 };
  });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState({
    rows: [],
    pagination: { current: 1, pageSize: 15, total: 0 },
    filters: {},
  });

  const load = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const res = await reportsApi.getFinanceReport(nextFilters);
      setData({
        rows: extractRows(res),
        pagination: extractPagination(res),
        filters: res?.filters || {},
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) return;
    const timer = setTimeout(() => load(filters), 350);
    return () => clearTimeout(timer);
  }, [filters, load]);

  const rows = data.rows;
  const chartData = useMemo(
    () =>
      rows.map((r, i) => ({
        label: r?.date || r?.paid_on || String(i + 1),
        amount: toNumber(r?.amount || r?.invoice_value || r?.amount_paid),
      })),
    [rows],
  );
  const totalAmount = useMemo(
    () => rows.reduce((acc, r) => acc + toNumber(r?.amount || r?.invoice_value || r?.amount_paid), 0),
    [rows],
  );

  return (
    <ReportPageLayout
      headerIcon={<LineChartOutlined />}
      title="گزارش مالی"
      subtitle="نمایش وضعیت مالی در بازه انتخابی"
      onExport={async () => {
        setExportLoading(true);
        try {
          await reportsApi.exportFinanceReport(filters);
        } finally {
          setExportLoading(false);
        }
      }}
      exportLoading={exportLoading}
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={[16, 12]}>
            <Col xs={24} lg={10}>
              <Form.Item label="بازه تاریخ" className="mb-0">
                <PersianDatePicker
                  range
                  placeholder="انتخاب بازه از تاریخ تا تاریخ"
                  value={getDateRangePickerValue(filters.startDate, filters.endDate)}
                  onChange={(dates) => {
                    const next = parseDateRangeChange(dates);
                    setFilters((p) => ({ ...p, ...next, page: 1 }));
                  }}
                  style={{ minHeight: 36 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item label="پروژه" className="mb-0">
                <Select
                  value={filters.project_id}
                  onChange={(v) => setFilters((p) => ({ ...p, project_id: v, page: 1 }))}
                  options={[
                    { value: 'all', label: 'همه' },
                    ...(data.filters?.projects || []).map((i) => ({ value: i.id, label: i.project_name || i.name })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item label="مشتری" className="mb-0">
                <ClientSelect
                  value={filters.client_id}
                  includeInactive
                  allowClear={false}
                  leadingOptions={[{ value: 'all', label: 'همه' }]}
                  onChange={(v) => setFilters((p) => ({ ...p, client_id: v ?? 'all', page: 1 }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>

      <div className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-3 mb-5">
        <Statistic
          title="جمع مبلغ "
          value={totalAmount}
          formatter={(v) => <span className="tabular-nums custom-font">{formatReportMoneyFa(v)}</span>}
        />
      </div>

      <div className="mb-5">
        {loading && !chartData.length ? (
          <Skeleton active paragraph={{ rows: 8 }} className="p-2" />
        ) : chartData.length ? (
          <div className="h-60 w-full pl-0 sm:pl-1" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 6, right: 10, left: 0, bottom: 4 }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  tickMargin={6}
                  interval="preserveStartEnd"
                />
                <YAxis
                  width={88}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatReportMoneyFa(v)}
                  tickMargin={4}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<FinanceLineTooltip />} />
                <Line
                  type="monotone"
                  dataKey="amount"
                  name="مبلغ"
                  stroke={REPORT_CHART_COLORS.sky}
                  strokeWidth={2.25}
                  dot={{ r: 3, fill: REPORT_CHART_COLORS.sky, strokeWidth: 0 }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <ChartEmptyState />
        )}
      </div>

      {loading && !rows.length ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <Table
          rowKey={(r) => r.id || `${r.invoice_number}-${r.paid_on}`}
          loading={loading}
          dataSource={rows}
          scroll={{ x: 1100 }}
          className="custom-font"
          pagination={{
            ...data.pagination,
            onChange: (page, pageSize) => {
              const next = { ...filters, page, per_page: pageSize };
              setFilters(next);
            },
          }}
          columns={[
            { title: 'تاریخ', dataIndex: 'paid_on' },
            { title: 'شماره فاکتور', dataIndex: 'invoice_number' },
            { title: 'مشتری', dataIndex: 'client_name' },
            { title: 'ارزش فاکتور', dataIndex: 'invoice_value', render: formatReportTableAmount },
            { title: 'مبلغ پرداخت شده', dataIndex: 'amount_paid', render: formatReportTableAmount },
            { title: 'حساب بانکی', dataIndex: 'bank_account' },
          ]}
        />
      )}
    </ReportPageLayout>
  );
};

export default FinanceReport;
